import { z } from "zod";
import { EffectTypeList, ModifierUpdateOptionsList, TriggerTypeList } from "./effectTypes";

// Type lists as Zod schemas

const TriggerTypeSchema = z.enum(TriggerTypeList);
const ModifierUpdateTypeSchema = z.enum(ModifierUpdateOptionsList);
const EffectTypeSchema = z.enum(EffectTypeList);
const TargetCountTypeSchema = z.union([z.literal("all"), z.number()]);

const ValueSchema: z.ZodType<any> = z.lazy(() =>
    z.union([z.number(), RitualValueSchema, TargetCountValueSchema, ModifierValueSchema, PropertyValueSchema, UserValueSchema, MathValueSchema, StoredValueSchema])
);

const TargetCountValueSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("target-count"),
        target: TargetTypeSchema
    })
);

const StoredValueSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("stored-value"),
        key: z.string(),
    })
);

const ModifierValueSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("modifier-value"),
    })
);

const RitualValueSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("ritual-value"),
    })
);

const StoredCardSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("stored-card"),
        key: z.string(),
    })
);

const TargetTypeSchema: z.ZodType<any> = z.lazy(() =>
    z.union([TargetSchema, StoredCardSchema])
);

// Value schemas
const MathValueSchema = z.object({
    type: z.literal("math"),
    sign: z.enum(["*", "/", "+", "-"]),
    left: ValueSchema,
    right: ValueSchema,
});

const PropertyValueSchema = z.object({
    type: z.literal("property"),
    property: z.enum([
        "cost",
        "castThreshold",
        "focusTotal",
        "focus",
        "focusTemp",
        "universalId",
    ]),
    cardId: z.union([z.string(), StoredCardSchema]),
});

const UserValueSchema = z.object({
    type: z.literal("user"),
    max: ValueSchema,
    min: ValueSchema,
});

// Target schema
const TargetSchema = z.object({
    type: z.enum(["player", "card", "class"]),
    controller: z.enum(["self", "opponent", "target"]).optional(),
    id: z.union([z.literal("this"), ValueSchema]).optional(),
    idNot: z.union([z.literal("this"), ValueSchema]).optional(),
    zone: z.enum(["hand", "discard", "battlefield", "all"]).optional(),
    count: TargetCountTypeSchema.optional(),
});

// Effect schemas
const DirectEffectSchema = z.object({
    type: EffectTypeSchema,
    amount: z.union([z.number(), ValueSchema]).optional(),
    value: z.string().optional(),
    by: z.string().optional(),
    target: TargetTypeSchema,
    secondaryTarget: TargetTypeSchema.optional(),
});

const MultipleEffectSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("multiple"),
        set: z.array(EffectSchema),
    })
);

const IteratedEffectSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("iterated"),
        effect: EffectSchema,
        count: ValueSchema,
    })
);

const TriggerSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("trigger"),
        condition: TriggerTypeSchema,
        source: z.enum(["any", "SYSTEM", "cast", "play", "sustain", "passive"]).optional(),
        on: TargetTypeSchema,
        then: ModifierEffectSchema.optional(),
        and: EffectBaseSchema.optional(),
    })
)

const ModifierEffectSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        type: z.literal("modifier"),
        update: ModifierUpdateTypeSchema,
        effect: ValueSchema.optional(),
    })
)

// Main Effect union
const EffectBaseSchema = z.union([
    DirectEffectSchema,
    MultipleEffectSchema,
    IteratedEffectSchema,
    TriggerSchema
]);

const EffectSchema = z.lazy(() => EffectBaseSchema);

// Ability schema
const AbilitySchema = z.object({
    playerId: z.string(),
    effect: EffectSchema,
    source: z.enum(["cast", "play", "sustain", "passive"]),
    card: z.any(),
    count: z.number(),
});

export {
    TriggerTypeSchema,
    ModifierValueSchema,
    EffectTypeSchema,
    TargetCountTypeSchema,
    MathValueSchema,
    PropertyValueSchema,
    UserValueSchema,
    StoredValueSchema,
    TargetSchema,
    TargetTypeSchema,
    DirectEffectSchema,
    MultipleEffectSchema,
    IteratedEffectSchema,
    EffectBaseSchema,
    EffectSchema,
    AbilitySchema,
    TargetCountValueSchema,
};